import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { PROJECT_PANEL } from "../../../utils/constants";
import { useSelector } from "react-redux";
import handleError from "../../../utils/handleError";
import {
  saveClientData,
  checkNameAvailability,
} from "../../../services/admin/client/client";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ProgressSpinner } from "primereact/progressspinner";
import { getClientDetails } from "../../../services/admin/client/client";
import { useParams } from "react-router-dom";
import { decrypt } from "../../../utils/commonLogics";
import { Toast } from "primereact/toast";
import { toSentenceCase } from "../../../utils/constants";
import { validateEmail } from "../../../globalUtilities/validation";
import { getUserInfo, saveUserData } from "../../../services/user/manageusers";

const CreateUser = () => {
  const { id } = useParams();
  const toast = useRef(null);
  const [first_name_error, setFirstNameError] = useState("");
  const [last_name_error, setLastNameError] = useState("");
  const [user_email_error, setUserEmailError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { token, user } = useSelector((state) => state.auth);
  let match;
  let clientId;
  let userId;
  if (id && location.pathname.includes("edit") && user.type === "superadmin") {
    match = location.pathname.match(/fromClient-(\d+)-(\d+)/);
    clientId = match ? match[1] : null;
    userId = match ? match[2] : null;
  } else if (
    id &&
    location.pathname.includes("edit") &&
    (user.type === "client" || user.type === "admin_user")
  ) {
    match = location.pathname.match(/fromClient-(\d+)-(\d+)/);
    // clientId = match ? match[1] : null;
    userId = id;
  } else {
    //handle clientId for superadmin user create
    match = location.pathname.match(/fromClient-(\d+)/);
    clientId = match ? match[1] : null;
  }

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const showError = (message) => {
    toast.current.show({
      severity: "error",
      summary: "Error",
      detail: message,
      life: 3000,
    });
  };

  useEffect(() => {
    if (id && location.pathname.includes("edit")) {
      const formData = new FormData();
      formData.append("admin_user_id", userId);
      formData.append("type", user.type);
      getUserInfo(token, formData)
        .then((response) => {
          if (response?.data?.client_data) {
            const client = response?.data?.client_data;
            setFormData({
              first_name: client.first_name || "",
              last_name: client.last_name || "",
              user_email: client.email || "",
              permissions: client.project_access?.split(",") || [],
            });
          }
          setLoading(false);
        })
        .catch((error) => {
          setLoading(false);
          handleError(error, navigate, dispatch);
        });
    } else {
      setLoading(false);
    }
  }, [id, token, user.id, user.type, navigate, dispatch]);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    user_email: "",
    permissions: ["collaborate"],
  });

  const capitalizeFirstChar = (text) => {
    if (!text) return "";
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const debouncedCheckNameAvailability = useCallback(
    _.debounce((name) => {
      const reqBody = new FormData();
      reqBody.append("name", name);

      checkNameAvailability(token, reqBody)
        .then((response) => {
          const inputElement = document.getElementById("first_name");
          if (response?.data?.success === 0 && response?.data?.exists) {
            setFirstNameError("A user with this name already exists.");
            if (inputElement) inputElement.style.background = "#f8d7da";
          } else {
            setFirstNameError("");
            if (inputElement) inputElement.style.background = "";
          }
        })
        .catch((error) => {
          handleError(error, navigate, dispatch);
        });
    }, 500),
    [token, navigate, dispatch],
  );

  const handleChange = (e, type = "input") => {
    if (isSubmitting) return;

    if (type === "checkbox") {
      const { value, checked } = e.target;

      if (checked) {
        setFormData((prev) => {
          return { ...prev, permissions: [...prev.permissions, value] };
        });
      } else {
        setFormData((prev) => {
          return {
            ...prev,
            permissions: prev.permissions.filter((item) => item !== value),
          };
        });
      }
    } else {
      const { id, value } = e.target;
      const trimmedValue = value.trim();

      setFormData((prev) => ({
        ...prev,
        ...{ [id]: value },
      }));

      if (!trimmedValue) {
        const errorText = `${capitalizeFirstChar(id.replace("_", " "))} cannot be empty.`;

        if (id === "first_name") {
          setFirstNameError(errorText);
          const inputElement = document.getElementById(id);
          if (inputElement) inputElement.style.background = "";
        } else if (id === "last_name") {
          setLastNameError(errorText);
        } else if (id === "user_email") {
          setUserEmailError(errorText);
        }

        return;
      } else {
        if (id === "first_name") {
          setFirstNameError("");
          const inputElement = document.getElementById(id);
          if (inputElement) inputElement.style.background = "";
        }
        if (id === "last_name") {
          setLastNameError("");
        }
        if (id === "user_email") {
          setUserEmailError("");
        }
      }

      if (id === "user_email") {
        if (!validateEmail(trimmedValue)) {
          setUserEmailError("Please enter valid email");
        } else {
          setUserEmailError("");
        }
      }

      // if (id === "first_name") {
      //   debouncedCheckNameAvailability(trimmedValue);
      // }

      // if (id === "last_name" && trimmedValue.length) {
      //   if (window.citySuggestionDebounceTimeout) {
      //     clearTimeout(window.citySuggestionDebounceTimeout);
      //   }

      //   window.citySuggestionDebounceTimeout = setTimeout(() => {
      //     getCitySuggestionsForClient(trimmedValue);
      //   }, 500);
      // }
    }
  };

  const handleSaveUser = async () => {
    if (isSubmitting) return;

    let firstErrorField = null;

    if (!formData.first_name.trim()) {
      setFirstNameError("Please enter a user first name.");
      firstErrorField = firstErrorField || "first_name";
    } else if (first_name_error) {
      firstErrorField = firstErrorField || "first_name";
    } else {
      setFirstNameError("");
    }

    if (!formData.last_name.trim()) {
      setLastNameError("Please enter a user last name.");
      firstErrorField = firstErrorField || "last_name";
    } else if (last_name_error) {
      firstErrorField = firstErrorField || "last_name";
    } else {
      setLastNameError("");
    }

    if (!formData.user_email.trim()) {
      setUserEmailError("Please enter the user email address.");
      firstErrorField = firstErrorField || "user_email";
    } else if (user_email_error) {
      firstErrorField = firstErrorField || "user_email";
    } else {
      setUserEmailError("");
    }

    if (firstErrorField) {
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus();
        // if (firstErrorField === "first_name") {
        //   if (first_name_error) {
        //     element.style.background = "#f8d7da";
        //   } else {
        //     element.style.background = "";
        //   }
        // } else {
        //   element.style.background = "";
        // }
      }
      return;
    }

    setIsSubmitting(true);

    const multipartData = new FormData();
    multipartData.append("client_first_name", formData.first_name || "");
    multipartData.append("client_last_name", formData.last_name || "");
    multipartData.append("client_email", formData.user_email || "");
    multipartData.append("permissions", formData.permissions.join(",") || "");
    multipartData.append(
      "client_id",
      user.type === "superadmin" ? clientId : user.client_id,
    );

    if (id && location.pathname.includes("edit")) {
      multipartData.append("admin_user_id", userId);
    }

    saveUserData(token, multipartData)
      .then((response) => {
        if (response?.data?.success === 1) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: response?.data?.message,
            life: 1000,
          });
          setTimeout(() => {
            if (user.type === "superadmin") {
              navigate(`/admin-panel/users/fromClient-${clientId}`);
            } else {
              navigate("/dashboard/users");
            }
          }, 1000);
        } else {
          console.error("Saving user data was unsuccessful.");
          showError("Saving user data was unsuccessful.");
          setIsSubmitting(false);
        }
      })
      .catch((error) => {
        setIsSubmitting(false);
        if (error?.response?.data?.message !== "Invalid Token") {
          showError(error?.response?.data?.message);
        } else {
          handleError(error, navigate, dispatch);
        }
      });
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div
      className="container-fluid page-wrapper"
      style={{ position: "relative" }}
    >
      <Toast ref={toast} />
      <div className={`row ${isSubmitting ? "pointer-events-none" : ""}`}>
        <div className="col-12 col-md-12">
          <div className="card shadow-card" style={{ minHeight: "590px" }}>
            <div className="d-flex justify-content-between align-items-center p-3">
              <h6 className="fw-bold m-0 title">
                {id && location.pathname.includes("edit")
                  ? "Edit User"
                  : "Create a User"}
              </h6>
            </div>
            <Divider style={{ margin: "0.5rem 0", padding: "0 1rem" }} />
            <div className="container mt-3">
              <div className="card shadow-card-2">
                <div className="row mt-2">
                  <div className="col-md-6 mb-2">
                    <label
                      htmlFor="first_name"
                      className="form-label float-start dark-text"
                    >
                      First name:
                    </label>
                    <InputText
                      id="first_name"
                      className={`w-100 inputField`}
                      autoComplete="off"
                      value={formData.first_name}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />
                    <div
                      className="text-danger mt-2 text-start"
                      id="first_name_error"
                    >
                      {first_name_error}
                    </div>
                  </div>
                  <div className="col-md-6 mb-2">
                    <label
                      htmlFor="last_name"
                      className="form-label float-start dark-text"
                    >
                      Last Name:
                    </label>
                    <InputText
                      id="last_name"
                      className={`w-100 inputField`}
                      autoComplete="off"
                      value={formData.last_name}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />
                    <div
                      className="text-danger mt-2 text-start"
                      id="last_name_error"
                    >
                      {last_name_error}
                    </div>
                  </div>
                </div>
                <div className="row mb-1">
                  <div className="col-md-6 mb-3">
                    <label
                      htmlFor="user_email"
                      className="form-label float-start dark-text"
                    >
                      Email Address:
                    </label>
                    <InputText
                      id="user_email"
                      className={`w-100 inputField`}
                      autoComplete="off"
                      value={formData.user_email}
                      onChange={handleChange}
                      disabled={
                        isSubmitting ||
                        (id && location.pathname.includes("edit"))
                          ? true
                          : false
                      }
                    />
                    <div
                      className="text-danger mt-2 text-start"
                      id="user_email_error"
                    >
                      {user_email_error}
                    </div>
                  </div>
                </div>
                <Divider />

                <h6 className="text-start mt-4 fw-600">Set Permissions</h6>
                <div className="col-md-4 col-sm-6 col-12 mt-2 mb-4 text-start">
                  <Checkbox
                    inputId={`collaborate`}
                    value={"collaborate"}
                    onChange={(e) => handleChange(e, "checkbox")}
                    checked={formData.permissions.includes("collaborate")}
                    disabled={isSubmitting}
                    readOnly
                  />
                  <label
                    htmlFor={`collaborate`}
                    className="ms-2"
                    title={toSentenceCase("collaborate")}
                    style={{
                      display: "inline-block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      verticalAlign: "bottom",
                      maxWidth: "90%",
                    }}
                  >
                    {toSentenceCase("collaborate")}
                  </label>

                  <Checkbox
                    inputId={`manage_users`}
                    value={"manage_users"}
                    className="ms-5"
                    onChange={(e) => handleChange(e, "checkbox")}
                    checked={formData.permissions.includes("manage_users")}
                    disabled={isSubmitting}
                  />
                  <label
                    htmlFor={`manage_users`}
                    className="ms-2 "
                    title={toSentenceCase("Manage Users")}
                    style={{
                      display: "inline-block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      verticalAlign: "bottom",
                      maxWidth: "90%",
                    }}
                  >
                    {toSentenceCase("Manage Users")}
                  </label>
                </div>

                <div className="d-flex justify-content-center gap-2 mt-5 mb-5">
                  <Button
                    label="Cancel"
                    className="px-4 py-2 fw-bold cancel"
                    onClick={() => {
                      if (user.type === "superadmin") {
                        navigate(`/admin-panel/users/fromClient-${clientId}`);
                      } else {
                        navigate("/dashboard/users");
                      }
                    }}
                    disabled={isSubmitting}
                  />
                  <Button
                    label={
                      <>
                        Save
                        {isSubmitting && (
                          <ProgressSpinner
                            style={{
                              width: "20px",
                              height: "20px",
                              marginLeft: "8px",
                            }}
                            strokeWidth="4"
                            fill="transparent"
                            animationDuration=".5s"
                          />
                        )}
                      </>
                    }
                    className="px-4 py-2 fw-bold createClientBtn d-flex align-items-center"
                    onClick={() => handleSaveUser()}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateUser;
